// File: components/account/AccountModal.js

/**
 * CDRisk Account modal.
 *
 * Signed out: password sign-in / create account, passkey sign-in, and
 * whichever social providers the API reports as enabled. CDRisk clients and
 * their employees use the same credentials as the client portal — that's the
 * SSO story; no separate account needed.
 * Signed in: shows who's signed in, syncs annotations, offers passkey
 * enrollment and sign-out.
 */

import React, { useEffect, useState } from 'react';
import { X, KeyRound, LogOut, Cloud, Loader2 } from 'lucide-react';
import {
  getAccount,
  signInWithPassword,
  registerAccount,
  signInWithPasskey,
  registerPasskey,
  passkeysSupported,
  fetchProviders,
  startOAuth,
  signOut,
} from '../../utils/account.js';
import { fullSync } from '../../utils/annotationSync.js';
import '../../styles/components/account.css';

const PROVIDER_LABELS = {
  google: 'Continue with Google',
  microsoft: 'Continue with Microsoft',
  apple: 'Continue with Apple',
};

function AccountModal({ open, onClose, initialError = '' }) {
  const [account, setAccount] = useState(getAccount());
  const [mode, setMode] = useState('signin'); // 'signin' | 'register'
  const [providers, setProviders] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setError(initialError);
  }, [initialError]);

  useEffect(() => {
    const update = () => setAccount(getAccount());
    window.addEventListener('cdrisk:account-changed', update);
    return () => window.removeEventListener('cdrisk:account-changed', update);
  }, []);

  useEffect(() => {
    if (open) fetchProviders().then(setProviders);
  }, [open]);

  if (!open) return null;

  const run = async (action, successNotice) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      if (successNotice) setNotice(successNotice);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'signin') {
      run(() => signInWithPassword(email, password));
    } else {
      run(() => registerAccount({ email, password, firstName, lastName }));
    }
  };

  return (
    <div className="account-overlay" role="dialog" aria-modal="true" aria-label="CDRisk Account">
      <div className="account-modal">
        <div className="account-header">
          <div>
            <h2 className="account-title">CDRisk Account</h2>
            <p className="account-subtitle">
              {account
                ? 'Bookmarks and notes sync to your account on every device.'
                : 'Sign in to keep your bookmarks and notes on every device. CDRisk clients and employees: use your existing CDRisk login.'}
            </p>
          </div>
          <button className="account-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {error && <div className="account-error">{error}</div>}
        {notice && <div className="account-notice">{notice}</div>}

        {account ? (
          <div className="account-signed-in">
            <p className="account-identity">
              Signed in as <strong>{account.user.name || account.user.email}</strong>
              <span className="account-email">{account.user.email}</span>
            </p>
            <button
              className="account-btn"
              disabled={busy}
              onClick={() => run(() => fullSync(), 'Annotations synced.')}
            >
              <Cloud size={16} /> Sync now
            </button>
            {passkeysSupported() && (
              <button
                className="account-btn"
                disabled={busy}
                onClick={() => run(() => registerPasskey(), 'Passkey added to this device.')}
              >
                <KeyRound size={16} /> Add a passkey to this device
              </button>
            )}
            <button
              className="account-btn account-btn-quiet"
              disabled={busy}
              onClick={() => {
                signOut();
                setNotice('Signed out. Your annotations stay on this device.');
              }}
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        ) : (
          <div className="account-signed-out">
            <form onSubmit={handleSubmit} className="account-form">
              {mode === 'register' && (
                <div className="account-row">
                  <input
                    className="account-input"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                  <input
                    className="account-input"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>
              )}
              <input
                className="account-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <input
                className="account-input"
                type="password"
                placeholder={mode === 'register' ? 'Password (8+ characters)' : 'Password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                minLength={8}
                required
              />
              <button className="account-btn account-btn-primary" type="submit" disabled={busy}>
                {busy ? <Loader2 size={16} className="account-spin" /> : null}
                {mode === 'signin' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            <button
              className="account-switch"
              onClick={() => {
                setMode(mode === 'signin' ? 'register' : 'signin');
                setError('');
              }}
            >
              {mode === 'signin'
                ? "Don't have an account? Create one"
                : 'Already have an account? Sign in'}
            </button>

            {(passkeysSupported() || providers.length > 0) && (
              <div className="account-divider">
                <span>or</span>
              </div>
            )}

            {passkeysSupported() && (
              <button
                className="account-btn"
                disabled={busy}
                onClick={() => run(() => signInWithPasskey())}
              >
                <KeyRound size={16} /> Sign in with a passkey
              </button>
            )}

            {providers.map((provider) => (
              <button
                key={provider}
                className={`account-btn account-btn-social account-btn-${provider}`}
                disabled={busy}
                onClick={() => startOAuth(provider)}
              >
                {PROVIDER_LABELS[provider] || `Continue with ${provider}`}
              </button>
            ))}
          </div>
        )}

        {/* Who holds the account, and under what. This modal offers to keep
            your bookmarks and notes "on every device", which means it is
            asking for an account on a server — and until now it never said
            whose server, or under what terms. One sentence, on the screen
            where the decision is made rather than in a policy two clicks
            away on a different domain. */}
        <p className="account-legal">
          Accounts are stored by Critical Dynamics Risk Management under its{' '}
          <a
            href="https://cdrisk.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}

export default AccountModal;
